import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button } from '@mui/material';
import axios from 'axios';
import ReaderContent from '../components/ReaderContent';
import ReaderSlider from '../components/ReaderSlider';
import { theme } from '../../../../theme';
import * as storiesApi from '../../../../api/stories';
import { saveNextUrl } from '../../../auth/domain/authNavigation';
import ReaderSiteHeader from '../../../../shared/ui/ReaderSiteHeader';

type ApiErrorResponseShape = {
  error?: string;
  statusCode?: number;
  exceptionType?: string;
  message?: string;
};

export function getAxiosError(err: unknown): { status?: number; message?: string } {
  if (!axios.isAxiosError(err)) {
    return { message: 'A server error occurred. Please try again later.' };
  }

  let data: any = err.response?.data;

  if (typeof data === 'string' && data.trim().startsWith('{')) {
    try {
      data = JSON.parse(data);
    } catch {
      // ignore
    }
  }

  const body = data && typeof data === 'object' ? (data as ApiErrorResponseShape) : undefined;
  const status = err.response?.status ?? body?.statusCode;

  const message =
    body?.error ??
    body?.message ??
    (typeof data === 'string' ? data : undefined) ??
    err.response?.statusText ??
    'A server error occurred. Please try again later.';

  return { status, message };
}

const ReaderPage = ({ fullscreen = false }: { fullscreen?: boolean }) => {
  const { id, pageIndex } = useParams();
  const loc = useLocation();
  const navigate = useNavigate();

  const [chapter, setChapter] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const isFree = useMemo(() => {
    const qs = new URLSearchParams(loc.search);
    return qs.get('free') === '1';
  }, [loc.search]);
  const backPath = ((loc.state as { fromPath?: string } | null)?.fromPath) || (isFree ? '/stories-notext' : '/stories');

  const redirectedRef = useRef<string>('');

  const openLogin = () => {
    if (!id) return;

    const redirectKey = `${id}|${isFree ? 'free' : 'paid'}`;
    if (redirectedRef.current === redirectKey) return;
    redirectedRef.current = redirectKey;

    const next = `/g/${id}${loc.search || ''}`;
    saveNextUrl(next);

    navigate(`/g/${id}/auth${loc.search || ''}`, { replace: true });
  };

  const fetchChapter = async (abortSignal?: AbortSignal) => {
    if (!id) return;

    setLoading(true);
    setErrorMsg(null);

    try {
      const response = isFree
        ? await storiesApi.getChapterFreeByIndex(id, { signal: abortSignal } as any)
        : await storiesApi.getChapterByIndex(id, { signal: abortSignal } as any);

      setChapter(response.data);
      redirectedRef.current = '';
    } catch (err: any) {
      if (axios.isCancel(err) || err?.name === 'CanceledError') return;

      const { status, message: apiMsg } = getAxiosError(err);

      if (status === 401 || status === 403) {
        setChapter(null);
        setErrorMsg('Please sign in to access this chapter.');
        openLogin();
        return;
      }

      const msg =
        apiMsg ||
        (status && status !== 500 ? 'Could not load the chapter.' : 'A server error occurred. Please try again later.');

      setChapter(null);
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  const prevKey = useRef<string>('');
  useEffect(() => {
    if (!id) return;

    const key = `${id}|${isFree ? 'free' : 'paid'}|${loc.search || ''}`;
    if (prevKey.current === key) return;
    prevKey.current = key;

    const controller = new AbortController();
    void fetchChapter(controller.signal);

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isFree, loc.search]);

  if (errorMsg) {
    return (
      <Box sx={{ minHeight: '100vh', background: theme.palette.background.default }}>
        <ReaderSiteHeader mode="reader" title="Reader" subtitle="Open a chapter, manage your session, and keep your place." />
        <Box
          sx={{
            minHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            px: 2,
            textAlign: 'center',
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 560, width: '100%' }}>
            {errorMsg}
          </Alert>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => fetchChapter()} disabled={loading} sx={{ minWidth: 160 }}>
              {loading ? 'Loading...' : 'Try again'}
            </Button>
            <Button variant="outlined" onClick={() => navigate(backPath)} sx={{ minWidth: 160 }}>
              Back to library
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  if (fullscreen && pageIndex && chapter) {
    return (
      <ReaderSlider
        images={chapter.pages?.items?.map((p: any) => p.page) || []}
        initialIndex={parseInt(pageIndex, 10)}
        onClose={() => window.history.back()}
      />
    );
  }

  if (!chapter) {
    return (
      <Box sx={{ minHeight: '100vh', background: theme.palette.background.default }}>
        <ReaderSiteHeader mode="reader" title="Reader" subtitle="Loading chapter details." />
        <Box
          sx={{
            minHeight: '70vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: theme.palette.background.default,
          }}
        >
          <Box>Loading...</Box>
        </Box>
      </Box>
    );
  }

  return (
    <ReaderContent
      title={chapter.title}
      description={chapter.description}
      currentChapterId={id}
      lastUpdate={new Date(chapter.lastUpdate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })}
      pages={chapter.pages}
    />
  );
};

export default ReaderPage;
